import React from "react";
import useAnalyzeReportQuery from "../useQuery/useAnalyzeReportQuery";
import {
  Box,
  Typography,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Stack,
  CircularProgress,
} from "@mui/material";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import {
  sev,
  confColor,
  Section,
  Callout,
  Field,
  StatCard,
  IconList,
  Centered,
} from "./AnalyzeReportComponents";


const AnalyzeReportPage = () => {
  const params = new URLSearchParams(window.location.search);
  const tcid = params.get("tcid");

const {
    data: analysis,
    isLoading,
    isError,
  } = useAnalyzeReportQuery({
    tcid,
    description: params.get("description"),
    testcasename: params.get("testcasename"),
    errorstep: params.get("errorstep"),
    checktillsteps: params.get("checktillsteps"),
    option: params.get("option"),
  });

  if (isLoading) {
    return (
      <Centered>
        <CircularProgress sx={{ color: "#FF5336" }} />
        <Typography sx={{ fontSize: 16, fontWeight: 600 }}>Analyzing test failure…</Typography>
        <Typography sx={{ fontSize: 13, color: "#777" }}>
          This usually takes a few seconds.
        </Typography>
      </Centered>
    );
  }

  if (!tcid || isError) {
    return (
      <Centered>
        <Typography sx={{ fontSize: 16, fontWeight: 600 }}>No analysis data available</Typography>
        <Typography sx={{ fontSize: 13, color: "#777", textAlign: "center" }}>
          We couldn't load this analysis. Please run "Analyze" again from the test report.
        </Typography>
      </Centered>
    );
  }

  const { steps = [], finalAnalysis, cost } = analysis ?? {};

  return (
    <Box sx={{ bgcolor: "#f5f5f5", minHeight: "100vh", p: { xs: 2, md: 4 } }}>
      <Box sx={{ maxWidth: 1600, mx: "auto" }}>
        {/* Root Cause */}
        <Section
          title="Root Cause Analysis"
          icon={<ReportProblemOutlinedIcon />}
          gradient="linear-gradient(90deg, #e53e51, #c62828)"
        >
          <Callout bg="#fdecea" border="#e53e51">
            <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>Root Cause</Typography>
            <Typography sx={{ fontSize: 14 }}>
              {finalAnalysis?.actual_root_cause ?? "N/A"}
            </Typography>
          </Callout>

          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
            {finalAnalysis?.actual_failure_category && (
              <Chip size="small" label={`Category: ${finalAnalysis.actual_failure_category}`} />
            )}
            {finalAnalysis?.actual_failure_subcategory && (
              <Chip
                size="small"
                variant="outlined"
                label={`Subcategory: ${finalAnalysis.actual_failure_subcategory}`}
              />
            )}
            {finalAnalysis?.confidence && (
              <Chip
                size="small"
                color={confColor(finalAnalysis.confidence)}
                label={`Confidence: ${finalAnalysis.confidence}`}
              />
            )}
            {finalAnalysis?.earliest_meaningful_deviation_step != null && (
              <Chip
                size="small"
                color="error"
                variant="outlined"
                label={`Earliest deviation: step ${finalAnalysis.earliest_meaningful_deviation_step}`}
              />
            )}
          </Stack>

          {finalAnalysis?.most_likely_corrective_action && (
            <Callout bg="#edf7ed" border="#2e7d32">
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <BuildOutlinedIcon sx={{ fontSize: 18, color: "#2e7d32" }} />
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Corrective Action</Typography>
              </Stack>
              <Typography sx={{ fontSize: 14 }}>
                {finalAnalysis.most_likely_corrective_action}
              </Typography>
            </Callout>
          )}

          {finalAnalysis?.earliest_meaningful_deviation_reason && (
            <Typography sx={{ fontSize: 13, color: "#555" }}>
              {finalAnalysis.earliest_meaningful_deviation_reason}
            </Typography>
          )}
        </Section>

        {/* Cost Summary */}
        <Section
          title="Cost Summary"
          icon={<PaidOutlinedIcon />}
          gradient="linear-gradient(90deg, #1976d2, #1565c0)"
        >
          <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap", gap: 2 }}>
            <StatCard
              label="LLM Calls"
              value={cost?.llm_calls}
              accent="#1976d2"
              icon={<PaidOutlinedIcon sx={{ fontSize: 18, color: "#1976d2" }} />}
            />
            <StatCard label="Total Tokens" value={cost?.total_tokens} accent="#26a69a" />
            <StatCard
              label="Total Cost"
              value={cost?.total_cost_usd != null ? `$${cost.total_cost_usd}` : null}
              accent="#FF5336"
            />
          </Stack>

          <Table size="small" sx={{ border: "1px solid #e0e0e0" }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "#fafafa" }}>
                <TableCell sx={{ fontWeight: 700 }}>Step</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Input</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Output</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Total</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(cost?.step_costs || []).map((sc) => (
                <TableRow key={sc.step_number} sx={{ "&:hover": { bgcolor: "#f5f9ff" } }}>
                  <TableCell>{sc.step_number}</TableCell>
                  <TableCell align="right">{sc.input_tokens}</TableCell>
                  <TableCell align="right">{sc.output_tokens}</TableCell>
                  <TableCell align="right">{sc.total_tokens}</TableCell>
                  <TableCell align="right">${sc.total_cost_usd}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>

        {/* Step Analysis */}
        <Section
          title="Step Analysis"
          icon={<TimelineOutlinedIcon />}
          gradient="linear-gradient(90deg, #1f2937, #111827)"
        >
          <Stack spacing={2}>
            {steps.map((s) => {
              const sc = sev(s.severity);
              return (
                <Paper
                  key={s.step_number}
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 1.5,
                    borderLeft: `4px solid ${sc.color}`,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                    transition: "box-shadow .2s",
                    "&:hover": { boxShadow: "0 2px 10px rgba(0,0,0,0.12)" },
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: "wrap" }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                      Step {s.step_number}
                    </Typography>
                    <Chip
                      size="small"
                      label={sc.label}
                      sx={{ bgcolor: sc.color, color: "#fff", fontWeight: 600 }}
                    />
                    {s.is_significant_deviation && (
                      <Chip size="small" color="error" variant="outlined" label="Significant deviation" />
                    )}
                    {s.confidence && (
                      <Chip size="small" variant="outlined" label={`Confidence: ${s.confidence}`} />
                    )}
                  </Stack>

                  <Field label="Summary" value={s.summary} />
                  <Field label="Category" value={s.failure_category} />

                  {s.root_cause && (
                    <Callout bg="#fdecea" border="#e53e51">
                      <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.25 }}>
                        Root Cause
                      </Typography>
                      <Typography sx={{ fontSize: 13 }}>{s.root_cause}</Typography>
                    </Callout>
                  )}

                  {s.deviations?.length > 0 && (
                    <IconList
                      title="Deviations"
                      items={s.deviations}
                      icon={<WarningAmberOutlinedIcon sx={{ fontSize: 16 }} />}
                      color="#ed6c02"
                    />
                  )}
                  {s.recommendations?.length > 0 && (
                    <IconList
                      title="Recommendations"
                      items={s.recommendations}
                      icon={<LightbulbOutlinedIcon sx={{ fontSize: 16 }} />}
                      color="#2e7d32"
                    />
                  )}
                  {s.evidence?.length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                        <FactCheckOutlinedIcon sx={{ fontSize: 16, color: "#555" }} />
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Evidence</Typography>
                      </Stack>
                      <Box sx={{ bgcolor: "#f7f7f7", borderRadius: 1, p: 1.5 }}>
                        {s.evidence.map((ev, i) => (
                          <Typography
                            key={i}
                            sx={{ fontSize: 12, fontFamily: "monospace", color: "#444", mb: 0.5, wordBreak: "break-word" }}
                          >
                            {ev}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Paper>
              );
            })}
          </Stack>
        </Section>
      </Box>
    </Box>
  );
};

export default AnalyzeReportPage;